import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { selectLoggedInUserState, selectSubscriptionsState } from '@store';
import { showError } from '@utils';
import { useAssignees, useServicePoints } from '@hooks';
import { useQuery } from 'react-query';
import { QUERY_KEYS } from 'constants/queryKeys';
import { agentsServices, subscriptionsServices } from '@services';
import { AGENT_ROLES, SUBSCRIBER_ROLES } from '@constants';
import {
  getAgentsRequestPayload,
  handleDriversData,
  handleSubscription,
  handleAssigneesData,
  handleSubscriptionStatusesOptions,
} from './utils';
import { SUBSCRIPTION_STATUSES_OPTIONS } from './constants';

export const useSubscriptionOperations = (fetchLinkedSubs = true) => {
  const { getSubscriptionHasErrors, getSubscriptionErrorMessage } = useSelector(
    selectSubscriptionsState
  );
  const { servicePointsMap, servicePointsWithDefaultLocations } =
    useServicePoints();
  const loggedInUser = useSelector(selectLoggedInUserState);
  const router = useRouter();
  const tab = router.query?.tab;

  const { assignees, assigneesMap } = useAssignees();

  const subscriptionId = router.query?.subscriptionId;

  const enumsResponse = useQuery([QUERY_KEYS.GET_SUBSCRIPTIONS_ENUMS_KEY], () =>
    subscriptionsServices.getSubscriptionsEnums()
  );

  const driverAgentsResponse = useQuery(
    [QUERY_KEYS.GET_SUBSCRIPTION_ASSIGNEES_KEY],
    () => {
      const payload = getAgentsRequestPayload({
        role: [
          AGENT_ROLES.isDriver,
          AGENT_ROLES.isRepairsMaintenanceAssociate,
        ].join(','),
      });
      return agentsServices.getFilterAgents({
        params: { page: 1, pageSize: 1000 },
        payload,
      });
    },
    {
      enabled: !!subscriptionId,
    }
  );

  const subscriptionResponse = useQuery(
    [QUERY_KEYS.GET_SUBSCRIPTION_KEY, subscriptionId],
    () => subscriptionsServices.getSubscriptionById(subscriptionId),
    {
      enabled:
        !!subscriptionId &&
        enumsResponse?.isFetched &&
        driverAgentsResponse?.isFetched,
    }
  );

  const linkedItemsResponse = useQuery(
    [QUERY_KEYS.GET_SUBSCRIPTION_LINKED_ITEMS_KEY, subscriptionId],
    () =>
      subscriptionsServices.getSubscriptionLinkedItems(subscriptionId, {
        page: 1,
        pageSize: 500,
        order: 'desc',
      }),
    {
      enabled: fetchLinkedSubs && !!subscriptionId,
    }
  );

  const agent = assignees?.find((item) => item.email === loggedInUser?.email);

  const linkedItemsData = linkedItemsResponse?.data?.data || [];
  const linkedItemsTotal = linkedItemsResponse?.data?.meta?.total || 0;

  const driverAgents = handleDriversData(
    driverAgentsResponse?.data?.data || []
  );

  const subscription = handleSubscription(
    subscriptionResponse?.data?.data || {},
    linkedItemsData,
    linkedItemsTotal,
    enumsResponse?.data?.data?.reasons_of_returning || [],
    enumsResponse?.data?.data?.deliveryStatuses || [],
    driverAgents || [],
    assigneesMap || {},
    servicePointsMap || {},
    enumsResponse?.data?.data?.outOfSla || []
  );

  const ownerEmail =
    subscription?.subscribers?.find((subscriber) => {
      return subscriber?.subscriptions?.find(
        (sub) =>
          sub.role === SUBSCRIBER_ROLES.owner.value && sub.id === subscriptionId
      );
    })?.email || '';

  const subscriptionContractsResponse = useQuery(
    [QUERY_KEYS.GET_SUBSCRIPTION_CONTRACTS_KEY, subscriptionId],
    () => subscriptionsServices.getSubscriptionContracts(subscriptionId),
    {
      // only in subscription details page fetchLinkedSubs is true
      enabled: !!subscriptionId && fetchLinkedSubs,
    }
  );

  const subscriptionStatusesOptions = useMemo(() => {
    return handleSubscriptionStatusesOptions(
      subscription?.deliveryInfo?.status,
      [...SUBSCRIPTION_STATUSES_OPTIONS]
    );
  }, [subscription?.deliveryInfo?.status]);

  const subscriptionAssigneesOptions = useMemo(() => {
    return handleAssigneesData(assignees || []);
  }, [assignees]);

  const customerTypesOptions = useMemo(() => {
    return enumsResponse?.data?.data?.customerTypes?.map((type) => ({
      text: type.name,
      value: type.id,
    }));
  }, [enumsResponse?.data?.data?.customerTypes]);

  const reasonsOfReturningOptions = subscription?.reasonsOfReturningOptions;

  useEffect(() => {
    if (getSubscriptionErrorMessage) {
      showError(getSubscriptionErrorMessage);
    }
  }, [getSubscriptionErrorMessage]);

  useEffect(() => {
    if (subscriptionResponse?.error) {
      showError(subscriptionResponse?.error);
    }
  }, [subscriptionResponse?.error]);

  useEffect(() => {
    if (linkedItemsResponse?.error) {
      showError(linkedItemsResponse?.error);
    }
  }, [linkedItemsResponse?.error]);

  useEffect(() => {
    if (enumsResponse?.error) {
      showError(enumsResponse?.error);
    }
  }, [enumsResponse?.error]);

  useEffect(() => {
    if (subscriptionContractsResponse?.error) {
      showError(subscriptionContractsResponse?.error);
    }
  }, [subscriptionContractsResponse?.error]);

  const loading =
    subscriptionResponse?.isFetching ||
    linkedItemsResponse?.isFetching ||
    enumsResponse?.isFetching ||
    subscriptionContractsResponse?.isFetching;

  const getSubscriptionAndContracts = () => {
    subscriptionResponse?.refetch();
    subscriptionContractsResponse?.refetch();
  };

  return {
    tab,
    agent,
    loading,
    subscriptionId,
    subscription,
    driverAgents,
    ownerEmail,
    assignees,
    subscriptionContracts: subscriptionContractsResponse?.data?.data || [],
    getSubscriptionHasErrors,
    getSubscriptionErrorMessage,
    subscriptionAssigneesOptions,
    subscriptionStatusesOptions,
    subscriptionRaw: subscriptionResponse?.data?.data || {},
    reasonsOfReturningOptions,
    outOfSlaOptions: enumsResponse?.data?.data?.outOfSla || [],
    customerTypesOptions,
    servicePointsWithDefaultLocations,
    getSubscription: subscriptionResponse?.refetch,
    getSubscriptionAndContracts,
  };
};
